import * as Sentry from '@sentry/nextjs';
import { AUTH, functions, httpsCallable } from '@util/firebase';
import { getHostUrl } from '@util/index';
import { UserDocument } from '@util/types/firestore/users';
import { FirebaseError } from 'firebase/app';
import { omnisendIdentify } from '@util/analytics/omnisend';
import {
  getUserDataForDL,
  setMetaAdvancedMatching,
  clearMetaAdvancedMatching,
  isPixelIdentityAttached,
} from '@util/analytics/analytics.service';
import {
  AuthErrorCodes,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
  UserCredential,
  sendEmailVerification as _sendEmailVerification,
  sendPasswordResetEmail as _sendPasswordResetEmail,
  updatePassword,
  ActionCodeSettings,
  confirmPasswordReset,
  applyActionCode,
  fetchSignInMethodsForEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateEmail,
  signInWithCustomToken,
  onIdTokenChanged,
  signInWithEmailLink,
  isSignInWithEmailLink,
} from 'firebase/auth';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { createContext, ReactNode, useState } from 'react';
import { listenToUserDocument, updateLastLogin, syncEmailVerified, fetchUserDocumentFromServer } from '@util/firestore/users';
import { useOnlineStatus } from '@util/hooks/useOnlineStatus';
import Cookie from 'js-cookie';
import { logError } from '@util/logError';
import { clearSessionId } from '@c/checkout-v3/CheckoutSessionService';
import { clearGuestIdentity } from '@util/analytics';
import { AUTH_COOKIE_OPTS } from '@c/authentication/authCookieOptions';

export { AUTH_COOKIE_OPTS } from '@c/authentication/authCookieOptions';
interface AuthProviderProps {
  children: ReactNode;
}

export interface Credentials {
  email: string;
  password: string;
}

export type FirebaseAuthFn<T extends Credentials | AuthProvider> = (
  args: T
) => Promise<FirebaseError | UserCredential>;

export const EMAIL_ERRORS = [
  AuthErrorCodes.USER_DELETED,
  AuthErrorCodes.EMAIL_EXISTS,
  AuthErrorCodes.INVALID_EMAIL,
  AuthErrorCodes.UNVERIFIED_EMAIL,
  AuthErrorCodes.INVALID_RECIPIENT_EMAIL,
];

export const PASSWORD_ERRORS = [
  AuthErrorCodes.WEAK_PASSWORD,
  AuthErrorCodes.INVALID_PASSWORD,
];

type AuthProvider = 'google' | 'apple';

// GB #1534: listenToUserDocument's onSnapshot listener is killed by Firestore
// itself the moment its error callback fires (deadline-exceeded, unavailable
// on flaky mobile) — nothing re-attaches until the next hourly token refresh,
// so a user can be stranded on isLoadingUserDoc's spinner indefinitely,
// including at checkout. This wrapper reacts ONLY to that confirmed
// mechanism (an explicit onTransportError) with bounded exponential backoff
// + jitter, so a sustained outage doesn't retry forever.
//
// DELIBERATELY DOES NOT attempt to also catch a silent hang (no doc, no
// error — e.g. #1462's AppCheck-throttle finding) via a wall-clock stall
// watchdog. A first version tried exactly that and was rejected by /qa-review
// before merge: `listenToUserDocument`'s own GB #1435 D1 cold-cache
// suppression deliberately fires NO callback while waiting for the server on
// a slow connection (user.service.ts, the `fromCache && !exists()` branch) —
// this repo has no persistent Firestore cache configured (src/utils/firebase.ts),
// so that branch is a routine hot-path condition, not an edge case. A stall
// watchdog cannot tell "the listener is dead" from "the listener is exactly
// doing its designed job, slowly" — it would tear down and abandon listeners
// Firestore itself would have self-healed, converting the targeted
// flaky-mobile population's *recoverable* spinner into an *unrecoverable*
// one once the 5-retry budget exhausts. Silent-hang coverage needs a signal
// this repo does not have yet (e.g. an onFirstEvent hook from
// listenToUserDocument that also fires from the suppression branch); that is
// separate, larger-blast-radius work, not bundled here.
//
// retryCount resets to 0 ONLY on a SERVER-CONFIRMED delivery
// (listenToUserDocument's new onServerConfirmedDelivery hook — fromCache
// strictly false), never on a plain doc callback. CORRECTION (FRESH EYES,
// found and proved with a control/probe harness before merge): an earlier
// version reset on ANY delivery, including one sourced from the LOCAL
// CACHE. On a resubscribe with a warm cache (any retry after the doc has
// already loaded once this session — the common case, not the rare one),
// Firestore delivers the cached doc via the ordinary callback path
// immediately, often before the watch stream has even reconnected — the
// exact mechanism user.service.ts's own D1 fix documents for a cold empty
// cache, just with real cached data instead of nothing. That reset the
// counter to 0 before the SAME underlying error recurred, defeating the
// entire retry cap: measured at 201 resubscribe attempts against a 20-error
// probe where the un-fixed version capped correctly at 6 against a harness
// that never delivered mid-retry. A flapping connection (deliver → error →
// deliver → error) has the identical defeat with no cache assumption needed
// at all. The fix distinguishes "some doc arrived" from "the server
// specifically confirmed the connection is alive" and resets only on the
// latter, so a persistently-broken watch stream that keeps re-serving a
// stale cached doc can no longer talk its way past the cap.
//
// A previous version of this file also reset retryCount on tab
// `visibilitychange` → visible. Removed (same review): it was dead exactly
// when it mattered (no live listener remains once retries are exhausted —
// nothing left to error again) and, before this fix, amplified the
// cache-defeat problem above during an active retry by adding a second,
// independent, connection-health-blind reset trigger. The
// onServerConfirmedDelivery fix above already gives a genuinely-recovered
// connection a fresh budget without needing a UI-focus proxy for it.
//
// Deliberately does NOT change isLoadingUserDoc's meaning — it still means
// exactly what it always has (user present, doc not yet received) and every
// existing consumer that ignores the new flag below keeps today's exact
// behavior (spinner) after retries exhaust.
//
// GB #1534 Phase 2: added a sibling `userDocError` boolean, set true only at
// terminal retry-cap exhaustion (via the onRetriesExhausted callback below)
// and cleared on the next delivery through this subscription's onDoc
// callback (see the call site in the onIdTokenChanged effect). Consumers opt
// in by reading it — resolveAuthLandingAction's 'wait' branch and
// checkout.tsx do; the rest (dashboard/orders, verify-email,
// FollowSellerButton, useDashboardAuthState) do not yet and are unaffected.
const USER_DOC_RETRY_BASE_MS = 1_000;
const USER_DOC_RETRY_MAX_MS = 30_000;
const USER_DOC_MAX_RETRIES = 5;

// Sentry's ingest quota is org-wide shared (the 2026-07-01→07-17 blackout —
// see GB #1526's cold-cache meter in user.service.ts, which hit this exact
// problem and is the template this mirrors). A single subscription only
// reports once (handleFailure returns after reporting, scheduling no further
// retries), but a sustained outage can span many subscription LIFETIMES on
// the same browser — hourly token refresh, repeated account switches — each
// independently exhausting its own cap and reporting again. This bounds one
// browser to one report per hour, same as the cold-cache meter's throttle.
const USER_DOC_RETRIES_EXHAUSTED_THROTTLE_KEY =
  'gf_user_doc_retries_exhausted';
const USER_DOC_RETRIES_EXHAUSTED_THROTTLE_MS = 60 * 60 * 1000;

/** Fails OPEN: a storage error must never silence the report. */
function userDocRetriesExhaustedThrottleAllows(now: number): boolean {
  try {
    const raw = window.localStorage.getItem(
      USER_DOC_RETRIES_EXHAUSTED_THROTTLE_KEY
    );
    if (raw === null || raw === '') return true;
    const last = Number(raw);
    if (!Number.isFinite(last)) return true;
    if (last <= now && now - last < USER_DOC_RETRIES_EXHAUSTED_THROTTLE_MS) {
      return false;
    }
    return true;
  } catch {
    return true;
  }
}

function subscribeToUserDocumentWithRetry(
  uid: string,
  onDoc: (doc: UserDocument | null) => void,
  onRetriesExhausted: () => void
): () => void {
  let cancelled = false;
  let retryCount = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let currentUnsubscribe: (() => void) | null = null;

  const clearRetryTimer = () => {
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const handleTransportError = () => {
    if (cancelled) return;
    // Firestore already killed the listener that errored (that's the whole
    // premise of this fix) — this call is a documented no-op in that case,
    // kept for defense in depth rather than trusted to have already run.
    currentUnsubscribe?.();
    currentUnsubscribe = null;
    if (retryCount >= USER_DOC_MAX_RETRIES) {
      try {
        const now = Date.now();
        if (userDocRetriesExhaustedThrottleAllows(now)) {
          Sentry.captureMessage(
            '[auth-lifecycle] user-doc-listener-retries-exhausted',
            {
              level: 'warning',
              extra: { uid, retries: retryCount },
            }
          );
          try {
            window.localStorage.setItem(
              USER_DOC_RETRIES_EXHAUSTED_THROTTLE_KEY,
              String(now)
            );
          } catch {
            // Storage unavailable — no persistent throttle for this browser,
            // but the report itself already went out above.
          }
        }
      } catch {
        // Telemetry must never break auth.
      }
      try {
        onRetriesExhausted();
      } catch {
        // A consumer's terminal-state callback must never break auth.
      }
      return; // Terminal: no further retry. onRetriesExhausted() is the only consumer-visible signal.
    }
    const delay =
      Math.min(
        USER_DOC_RETRY_BASE_MS * 2 ** retryCount,
        USER_DOC_RETRY_MAX_MS
      ) + Math.random() * 500;
    retryCount += 1;
    clearRetryTimer();
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (!cancelled) attempt();
    }, delay);
  };

  const attempt = () => {
    if (cancelled) return;
    currentUnsubscribe = listenToUserDocument(
      uid,
      (doc) => {
        if (cancelled) return;
        // NOTE: deliberately does NOT reset retryCount here — see the
        // module doc comment. A delivery through this callback alone (which
        // can be cache-sourced) is not proof the connection recovered; only
        // onServerConfirmedDelivery below is.
        onDoc(doc);
      },
      handleTransportError,
      () => {
        if (cancelled) return;
        retryCount = 0;
      }
    );
  };

  attempt();

  return function unsubscribeWithRetry() {
    cancelled = true;
    clearRetryTimer();
    currentUnsubscribe?.();
    currentUnsubscribe = null;
  };
}

interface AuthContext {
  user: User | null | undefined;
  userDoc: UserDocument | null;
  isLoadingUserDoc: boolean;
  // GB #1534 Phase 2: true once the user-doc listener's retry budget is
  // exhausted AND userDoc is still null (a sustained outage past ~1-2.5
  // min) — guarded so a flapping connection that already delivered a
  // cache-sourced doc (which doesn't reset the retry counter — see
  // subscribeToUserDocumentWithRetry's doc comment) can never set this for
  // an account that is actually loaded. isLoadingUserDoc stays true
  // regardless — this is a sibling signal for consumers that want to stop
  // waiting and show a terminal state instead. Cleared on the next delivery
  // through a fresh subscription, or on logout.
  userDocError: boolean;
  isLoggingIn: boolean;
  logout: () => Promise<void>;
  loginEmailPassword: FirebaseAuthFn<Credentials>;
  loginSocial: FirebaseAuthFn<AuthProvider>;
  createAccountEmailPassword: FirebaseAuthFn<Credentials>;
  signInWithToken: FirebaseAuthFn<Credentials>;
  sendPasswordlessLink: (email: string) => Promise<boolean>;
  signInPasswordlessWithLink: (
    email: string,
    url: string
  ) => Promise<UserCredential | FirebaseError>;
  sendEmailVerification: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => void;
  resetPassword: (oobCode: string, password: string) => Promise<void>;
  updateUserPassword: (oldPassword: string, password: string) => Promise<void>;
  isAccountEmailPassword: () => boolean;
  verifyEmail: (actionCode: string) => Promise<void>;
  fetchProviderForEmail: (email: string) => Promise<string | null>;
  updateUserEmail: (email: string) => void;
  refreshUserDoc: () => Promise<boolean>;
}
const AuthContext = createContext({} as AuthContext);

const initAuthProvider = (p: AuthProvider) => {
  if (p === 'apple') return new OAuthProvider('apple.com');
  return new GoogleAuthProvider();
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null | undefined>();
  const [userDoc, setUserDoc] = useState<UserDocument | null>(null);
  // GB #1534 Phase 2 — see subscribeToUserDocumentWithRetry's doc comment.
  const [userDocError, setUserDocError] = useState(false);
  // GB #1534 Phase 2 (FRESH EYES correction): mirrors `userDoc` for the
  // onRetriesExhausted callback below, which fires from inside a closure set
  // up once per onIdTokenChanged(user) — reading `userDoc` there directly
  // would see whatever it was AT SUBSCRIBE TIME, not its current value.
  // Updated on every render, so it's always current by the time the retry
  // budget (measured in tens of seconds) actually exhausts.
  const userDocRef = useRef<UserDocument | null>(null);
  userDocRef.current = userDoc;
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const router = useRouter();
  const { setOnlineStatus } = useOnlineStatus();
  // Track impersonation to skip updating online status (preserves real user's "last seen")
  const isImpersonatingRef = useRef(false);
  // Codex P1 fix (GB #1373): holds the unsubscribe for the CURRENT user-doc
  // Firestore listener so it can be detached before attaching a new one
  // (account switch) or on logout — see the onIdTokenChanged effect below.
  const userDocUnsubscribeRef = useRef<(() => void) | null>(null);
  // Codex P1 fix (final, post-merge review, GB #1373). Set true at the
  // start of the explicit logout() function below. Firebase's
  // onIdTokenChanged fires for ANY auth-state transition — including one
  // caused by THIS tab's own logout()'s signOut() call, not just a
  // different tab's logout. Without this guard, the passive listener's
  // gated reload() (see below) could race logout()'s own
  // window.location.assign('/') — whichever navigation call the browser
  // honors last wins, making the actual destination nondeterministic.
  //
  // RESET SEMANTICS (Codex P2 correction, final round) — this is the part
  // that matters: the flag is reset ONLY in logout()'s catch block, NEVER
  // on the success path. An earlier version reset it in a blanket
  // `finally`, which reopened the exact race this exists to prevent: the
  // `finally` runs immediately after window.location.assign('/') is
  // called, but assign() doesn't halt JS execution synchronously — the
  // browser schedules navigation while the current script keeps running.
  // If the passive listener's onIdTokenChanged(null) callback arrived in
  // that gap (after signOut() resolves, before the page actually
  // unloads) — plausible, since Firebase's internal auth-state propagation
  // isn't guaranteed to be perfectly synchronous with signOut()'s own
  // promise resolving — it would see the flag already reset to false and
  // fire its own reload(), racing the assign('/') that's already in
  // flight. On the success path this tab IS navigating away, so leaving
  // the flag true for the rest of its life is harmless — nothing here
  // checks it again before the page tears down. Only a genuine logout()
  // FAILURE (this tab is NOT navigating away) resets it, so the cross-tab
  // reload protection isn't permanently disabled for the rest of the
  // session in that case.
  //
  // Ref-scoped (not a true module-level variable, despite that being this
  // fix's original framing) so it resets cleanly per AuthProvider mount
  // instance rather than leaking across test cases that mount multiple
  // providers in the same module registry.
  const logoutInProgressRef = useRef(false);
  // GB #1435 (D2): set true by logout() immediately before signOut(). The
  // onIdTokenChanged(null) branch below destroys the auth cookie ONLY when
  // this is true. A transient null (iOS Safari tab resume can momentarily
  // lock IndexedDB and make Firebase report null for a still-valid session)
  // must NOT wipe the cookie — doing so forces a spurious re-login. Distinct
  // from logoutInProgressRef, which gates the cross-tab reload and is never
  // reset on the success path; this one IS reset after the cookie is removed.
  const isExplicitLogoutRef = useRef(false);
  // GB #1435 (D2): last auth state this listener saw, so the null branch can
  // tell a real session downgrade (worth logging) from an ordinary guest page
  // load (never had a user — not worth logging).
  const previousUserRef = useRef<User | null | undefined>(undefined);
  // GB #1435 (F4, QA review): signup flows (social AND email/password) sign
  // the user in BEFORE writing the Firestore doc, so the user-doc listener's
  // server read legitimately finds no doc for a moment. Stamp signup-capable
  // auth calls so the no-doc branch can tell "signup in flight" (routine,
  // breadcrumb only) from a genuine ghost account (alarm-worthy). The
  // /signup redirect itself is unaffected either way.
  const signupAuthStampRef = useRef(0);
  const SIGNUP_IN_FLIGHT_WINDOW_MS = 30_000;

  // Track loading state for user document
  // Note: We use the real-time listener (onIdTokenChanged effect) for fetching the user document
  // instead of a separate useQuery to avoid redundant Firestore reads and improve login performance.
  // The isLoadingUserDoc state is derived from whether we have a user but not yet a userDoc.
  const isLoadingUserDoc = user !== undefined && user !== null && userDoc === null;

  const sendEmailVerification = async () => {
    if (!AUTH.currentUser) throw new Error('No AUTH.currentUser');
    const actionCodeSettings: ActionCodeSettings = {
      url: `${getHostUrl()}/verify-email?verified=true`,
    };
    await _sendEmailVerification(AUTH.currentUser, actionCodeSettings);
  };

  const verifyEmail = useCallback((actionCode: string) => {
    return applyActionCode(AUTH, actionCode);
  }, []);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(AUTH, (user) => {
      // set user from email/password login
      setUser(user);
      // GB #1435 (D2): remember whether we previously held an authenticated
      // user before overwriting, so the null branch can distinguish a genuine
      // downgrade from a first-load guest visit.
      const hadUser = previousUserRef.current != null;
      // GB #1534 Phase 2 (adversarial review, M-2): captured before the
      // reassignment below overwrites it — see the userDocError switch
      // guard further down, which needs the uid this callback is
      // SUPERSEDING, not the one it just received.
      const previousUid = previousUserRef.current?.uid;
      previousUserRef.current = user;
      if (user && user.uid) {
        // Set Sentry user context so errors include user info
        Sentry.setUser({
          id: user.uid,
          email: user.email || undefined,
        });

        // Codex P1 fix (GB #1373): detach any previous user-doc listener
        // before attaching a new one. Without this, an account switch (or
        // any re-fire of onIdTokenChanged for a different uid) leaves the
        // OLD user's Firestore listener attached — a later snapshot on the
        // old doc would re-run setUserDoc()/setMetaAdvancedMatching() with
        // the OLD user's data and PII, after a logout or switch had already
        // cleared it. (The `return () => unsubscribeUserDoc()` that used to
        // sit below was dead code — onIdTokenChanged's callback is not a
        // React effect and Firebase never invokes that return value; it
        // looked like cleanup but never ran. This ref is what actually does
        // it now.)
        userDocUnsubscribeRef.current?.();

        // GB #1435 (Codex P1 on this PR): on an account SWITCH (including
        // admin signInWithToken impersonation), the D1 cold-cache suppression
        // means the NEW user's first snapshot may deliver nothing until the
        // server responds — during which the PREVIOUS user's doc would keep
        // rendering (name, roles, identity) against the new user's auth. Clear
        // a mismatched doc immediately so the switch shows the loading state,
        // never the wrong identity. Same-uid re-fires (hourly token refresh)
        // keep the doc — no flicker.
        setUserDoc((prev) => (prev && prev.uid !== user.uid ? null : prev));
        // GB #1534 Phase 2 (adversarial review, M-2): same switch-vs-refresh
        // distinction as the mismatched-doc clear above, applied to
        // userDocError — a genuine account SWITCH must not carry the
        // PREVIOUS user's terminal error state into the new session (e.g.
        // admin signInWithToken impersonation right after the outgoing
        // user's listener exhausted, even if that user's doc had never
        // loaded — deliberately keyed on the uid identity change itself,
        // not on whether a doc happened to exist, unlike the clear above).
        // A same-uid re-fire does NOT clear it here — this flag only clears
        // on an actual delivery (see onDoc below) or here on a real
        // identity change, never just because a fresh subscription starts.
        if (previousUid !== undefined && previousUid !== user.uid) {
          setUserDocError(false);
        }

        // Guards the callback below against firing after this listener has
        // been superseded (new user, logout, or unmount) — even for a
        // snapshot that was already in flight when unsubscribe() was called.
        // Firestore's unsubscribe stops FUTURE deliveries; it does not
        // retroactively cancel a callback invocation that's already queued.
        let isCurrentUserDocListener = true;

        // Listen to changes in the user document
        const unsubscribeUserDoc = subscribeToUserDocumentWithRetry(
          user.uid,
          (doc: UserDocument | null) => {
            if (!isCurrentUserDocListener) return;
            // GB #1534 Phase 2: any delivery here — including a cache-sourced
            // one — means this subscription is alive again, so clear a
            // terminal error a PRIOR subscription on this effect may have
            // set. Deliberately looser than retryCount's reset criterion
            // (which only trusts a server-confirmed delivery, to protect the
            // retry BUDGET from a flapping cache) — this is only a UI flag,
            // and hiding a stale error banner as soon as anything renders is
            // the safer UX default.
            setUserDocError(false);
            if (doc) {
              // GB #1534 Phase 2 (qa-review F1): update userDocRef
              // synchronously, not just via the render-time assignment below
              // — React 18 batches state updates from the same macrotask, so
              // if this delivery and a subsequent onRetriesExhausted land in
              // the same batch, the render-time assignment would not have
              // run yet and the exhaustion guard would see a stale (null)
              // ref, defeating it. Proved with a probe + positive control
              // (same-batch act() vs separate act() blocks) before merge.
              userDocRef.current = doc;
              // Update the userDoc state with the updated user document
              setUserDoc(doc);

              // Sync Firebase Auth emailVerified → Firestore is_email_verified
              // Handles OAuth users (auto-verified) and users who verified via email link while logged out
              // Skip if admin is impersonating another user
              if (
                user.emailVerified &&
                !doc.permissions?.is_email_verified &&
                !isImpersonatingRef.current
              ) {
                syncEmailVerified(doc.uid).catch((err) => {
                  // Log error but don't block login - sync is best-effort
                  console.error('[syncEmailVerified] Failed to sync for user:', doc.uid, err);
                });
              }

              // Set online status for presence tracking (skip if admin is impersonating)
              if (!isImpersonatingRef.current) {
                setOnlineStatus(doc.uid);
              }
              // Update Sentry with username once we have it
              Sentry.setUser({
                id: user.uid,
                email: user.email || undefined,
                username: doc.username || undefined,
              });
              // Identify user to Omnisend for campaign attribution (async, non-blocking)
              if (doc.email) {
                omnisendIdentify(doc.email, doc.first, doc.last);
              }
              // F2 / GB #1373: attach Manual Advanced Matching identity to the
              // Meta browser pixel so authenticated-user browser events (VC/ATC/IC)
              // carry identity, same as the server CAPI leg already does via
              // getUserDataForDL(). No-op if GPC/opt-out is active (checked inside).
              setMetaAdvancedMatching(getUserDataForDL(doc));
            } else {
              // User authenticated but has no document - redirect to signup to create one
              // This handles edge cases like social login where document creation failed.
              // GB #1435 (F4): rare by design (the D1 cold-cache suppression in
              // listenToUserDocument means this now only fires on a
              // server-confirmed missing doc), so alerting on it is desired.
              const signupInFlight =
                Date.now() - signupAuthStampRef.current <
                SIGNUP_IN_FLIGHT_WINDOW_MS;
              console.warn(
                '[auth-lifecycle] no-doc-signup-redirect',
                JSON.stringify({ uid: user.uid, signupInFlight })
              );
              if (signupInFlight) {
                // Routine new-signup window (auth precedes the doc write) —
                // breadcrumb only, or every social signup would pollute the
                // ghost-account alarm (QA review; Sentry 429 risk, I5).
                Sentry.addBreadcrumb({
                  category: 'auth-lifecycle',
                  message: 'no-doc-signup-redirect (signup in flight)',
                  level: 'info',
                  data: { uid: user.uid },
                });
              } else {
                // uid in extra, not tags: unbounded-cardinality tags are a
                // Sentry anti-pattern and Sentry.setUser already carries it.
                Sentry.captureMessage(
                  '[auth-lifecycle] no-doc-signup-redirect',
                  {
                    level: 'warning',
                    extra: { uid: user.uid },
                  }
                );
              }
              router.push('/signup');
            }
          },
          () => {
            // GB #1534 Phase 2: retry budget exhausted on THIS subscription.
            // Same staleness guard as onDoc above — a superseded subscription
            // (account switch, logout, unmount) must not set state for a
            // listener that's no longer current.
            if (!isCurrentUserDocListener) return;
            // FRESH EYES correction, found and proved with a probe + positive
            // control before merge: retryCount (the exhaustion trigger) and
            // userDoc (this consumer-visible flag's whole reason to exist)
            // are NOT the same signal. retryCount only resets on a
            // SERVER-CONFIRMED delivery (see the module doc comment above) —
            // a CACHE-sourced delivery already populates userDoc via onDoc's
            // setUserDoc(doc) without resetting retryCount. So a flapping
            // connection that keeps re-serving a cached doc while genuinely
            // failing to reconnect can populate userDoc AND still exhaust the
            // retry cap. Without this guard, userDocError would fire true for
            // an account that is actually loaded and working — exactly the
            // bug a zero-context review caught in checkout.tsx, which reads
            // this flag with no additional gate. Never surface a terminal
            // "connection problem" for a doc that already arrived.
            if (userDocRef.current) return;
            setUserDocError(true);
          }
        );
        userDocUnsubscribeRef.current = () => {
          isCurrentUserDocListener = false;
          unsubscribeUserDoc();
        };
        user.getIdToken().then((token) => {
          Cookie.set('auth', token, AUTH_COOKIE_OPTS);
        });
      } else if (user === null) {
        // GB #1435 (D2): capture the pre-teardown session signals BEFORE any
        // cookie removal below, so the downgrade log can be gated correctly.
        const explicitLogout = isExplicitLogoutRef.current;
        const hadCookie = !!Cookie.get('auth');

        setUserDoc(null);
        // GB #1534 Phase 2: don't leak a terminal error from this session
        // into the next login (or into the guest state after logout).
        setUserDocError(false);
        // GB #1435 (D2): only destroy the auth cookie on an EXPLICIT logout.
        // The cookie's embedded token expires within 1h and the middleware
        // rejects stale tokens, so leaving it in place is safe; removing it on
        // a transient null (iOS Safari tab resume) forces a spurious
        // re-login — the double-login bug this fix targets.
        if (explicitLogout) {
          Cookie.remove('auth');
          isExplicitLogoutRef.current = false;
        }
        // Clear Sentry user context on logout
        Sentry.setUser(null);
        // GB #1435 (D2): log only a genuine session downgrade (explicit
        // logout, a live auth cookie, or a user we were previously holding).
        // Ordinary guest page loads — no cookie, no prior user — are silent.
        if (explicitLogout || hadCookie || hadUser) {
          console.warn(
            '[auth-lifecycle] user-null',
            JSON.stringify({ explicitLogout, hadCookie, hadUser })
          );
        }
        // Codex P1 fix (GB #1373): detach the previous user's Firestore doc
        // listener BEFORE clearing pixel identity — see the comment above
        // listenToUserDocument() for why this matters.
        userDocUnsubscribeRef.current?.();
        userDocUnsubscribeRef.current = null;
        // F2 / GB #1373: clear pixel Advanced Matching so a shared/kiosk
        // browser doesn't keep attaching the previous user's identity to a
        // new anonymous session's events.
        //
        // Codex P2 fix: isolated in its own try/catch. This callback runs
        // inside Firebase's onIdTokenChanged dispatch, not our own function's
        // try/catch — a broken/hostile pixel (fbq exists but throws) must
        // never be able to disrupt auth-state handling here.
        //
        // Codex P1 fix (final, GB #1373): this branch also covers CROSS-TAB
        // LOGOUT — tab A calls logout() (which hard-navigates tab A away),
        // Firebase's shared auth state pushes user===null into tab B's
        // onIdTokenChanged listener too, but tab B was never told to
        // navigate anywhere. Without the reload below, tab B silently keeps
        // whatever Advanced Matching identity it had attached and keeps
        // sending it on every subsequent browser event — the exact
        // shared-device leak class the whole hard-nav design exists to
        // close, just reached via a different trigger than the explicit
        // logout() button. clearMetaAdvancedMatching() can't fix this on
        // its own (Phase A-E wire evidence: no in-page clear exists — see
        // its doc comment in analytics.service.ts), so mirror the cross-tab
        // opt-out listener's pattern exactly: reload, but ONLY when this
        // tab actually attached identity. Gated (not unconditional like the
        // explicit logout() path) because the overwhelmingly common case
        // hitting this branch is plain token expiry with no identity ever
        // attached, and reloading every such tab would be a pure UX cost
        // with zero privacy benefit. Loop-safe: after the reload this tab
        // is signed out, so nothing re-attaches and the flag stays false.
        //
        // Codex P1 fix (final, post-merge review, GB #1373): ALSO gated on
        // !logoutInProgressRef.current. This branch doesn't only cover a
        // DIFFERENT tab's logout — Firebase's onIdTokenChanged fires for
        // ANY auth-state transition, including one caused by THIS SAME
        // tab's own logout()'s signOut() call. Without this second gate,
        // this reload() could race logout()'s own window.location.assign('/')
        // — whichever navigation call the browser honors last wins, making
        // the actual destination nondeterministic. When logout() owns this
        // transition (ref is true for its full duration), it already owns
        // navigation — skip the reload here and let it handle things.
        try {
          clearMetaAdvancedMatching();
          if (!logoutInProgressRef.current && isPixelIdentityAttached()) {
            window.location.reload();
          }
        } catch (clearError) {
          logError(clearError);
        }
        // TASK-1373 / GB #1206: a guest who captured an email at checkout
        // and then someone logged out on that same browser would otherwise
        // leave that identity sitting in sessionStorage for the TTL window
        // (see getGuestIdentity) — the next shopper on this device could
        // inherit it on their own InitiateCheckout. Covers the listener
        // path (tab refresh, other-tab logout, token expiry) in addition
        // to the explicit logout() below.
        clearGuestIdentity();
      }
    });

    return () => {
      unsubscribe();
      // AuthProvider unmount cleanup (rare in practice — it wraps the app in
      // _app.tsx and effectively never unmounts mid-session, but this keeps
      // the ref's lifecycle fully correct rather than relying on that).
      userDocUnsubscribeRef.current?.();
      userDocUnsubscribeRef.current = null;
    };
  }, [setUser, setUserDoc]);

  const handleAuthError = (e: unknown) => {
    if (e instanceof FirebaseError) {
      return e;
    }
    logError(e);
    throw new Error('Encountered unknown error in auth flow');
  };

  const loginSocial: FirebaseAuthFn<AuthProvider> = async (args) => {
    try {
      setIsLoggingIn(true);
      // Social auth may be a first-time signup: the doc write happens AFTER
      // sign-in, so stamp the signup window for the no-doc alarm gate above.
      signupAuthStampRef.current = Date.now();
      const provider = initAuthProvider(args);
      const result = await signInWithPopup(AUTH, provider);
      // The doc write follows sign-in, so start its 30 s window when the popup completes.
      signupAuthStampRef.current = Date.now();
      setUser(result.user);
      // Set cookie immediately so middleware sees it before redirect
      const token = await result.user.getIdToken();
      Cookie.set('auth', token, AUTH_COOKIE_OPTS);
      // Track login timestamp in Firestore
      updateLastLogin(result.user.uid).catch(() => {});
      setIsLoggingIn(false);
      return result;
    } catch (e) {
      setIsLoggingIn(false);
      return handleAuthError(e);
    }
  };

  const loginEmailPassword: FirebaseAuthFn<Credentials> = async (args) => {
    try {
      setIsLoggingIn(true);
      const userCredential = await signInWithEmailAndPassword(
        AUTH,
        args.email,
        args.password
      );
      setUser(userCredential.user);
      // Set cookie immediately so middleware sees it before redirect
      const token = await userCredential.user.getIdToken();
      Cookie.set('auth', token, AUTH_COOKIE_OPTS);
      // Track login timestamp in Firestore
      updateLastLogin(userCredential.user.uid).catch(() => {});
      setIsLoggingIn(false);
      return userCredential;
    } catch (e) {
      setIsLoggingIn(false);
      return handleAuthError(e);
    }
  };

  // admin feature - impersonate another user
  const signInWithToken: FirebaseAuthFn<Credentials> = async (args) => {
    try {
      setIsLoggingIn(true);
      // Set impersonation flag to prevent updating the target user's "last seen" status
      isImpersonatingRef.current = true;
      const userCredential = await signInWithCustomToken(AUTH, args.email);
      setUser(userCredential.user);
      // Set cookie immediately so middleware sees it before redirect
      const token = await userCredential.user.getIdToken();
      Cookie.set('auth', token, AUTH_COOKIE_OPTS);
      setIsLoggingIn(false);
      return userCredential;
    } catch (e) {
      setIsLoggingIn(false);
      isImpersonatingRef.current = false;
      return handleAuthError(e);
    }
  };

  const sendPasswordlessLink = async (email: string): Promise<boolean> => {
    try {
      console.log('[sendPasswordlessLink]');
      const sendLink = httpsCallable<
        { email: string },
        { success: boolean; message: string }
      >(functions, 'users-sendPasswordlessLink');
      const result = await sendLink({ email });
      console.log('[sendPasswordlessLink] result');

      return result.data.success;
    } catch (e) {
      console.log('[sendPasswordlessLink] Error', e);
      setIsLoggingIn(false);
      throw handleAuthError(e);
    }
  };

  const signInPasswordlessWithLink = async (
    email: string,
    url: string
  ): Promise<UserCredential | FirebaseError> => {
    try {
      if (isSignInWithEmailLink(AUTH, url)) {
        const result = await signInWithEmailLink(AUTH, email, url);
        setUser(result.user);
        // Set cookie immediately so middleware sees it before redirect
        const token = await result.user.getIdToken();
        Cookie.set('auth', token, AUTH_COOKIE_OPTS);
        // Track login timestamp in Firestore
        updateLastLogin(result.user.uid).catch(() => {});
        return result;
      } else {
        throw new FirebaseError(
          'auth/invalid-action-code',
          'Invalid sign-in link.'
        );
      }
    } catch (error) {
      return handleAuthError(error);
    }
  };

  const createAccountEmailPassword: FirebaseAuthFn<Credentials> = async (
    args
  ) => {
    try {
      // Signup signs in before the Firestore doc write — stamp the window for
      // the no-doc alarm gate above.
      signupAuthStampRef.current = Date.now();
      const userCredential = await createUserWithEmailAndPassword(
        AUTH,
        args.email,
        args.password
      );
      return userCredential;
    } catch (e) {
      return handleAuthError(e);
    }
  };

  const logout = async () => {
    // Codex P1 fix (final, post-merge review, GB #1373): set at entry, and
    // — CRITICAL, Codex P2 correction — reset ONLY in the catch block
    // below, never on the success path. See logoutInProgressRef's doc
    // comment near its declaration for the full story: an earlier version
    // of this fix reset the flag in a blanket `finally`, which reopened the
    // exact race it exists to prevent. On success this tab is navigating
    // away (assign('/') below); the flag staying true for the rest of this
    // tab's life is harmless — nothing meaningful runs here after the page
    // starts unloading — and resetting it early, in the gap between
    // assign() being called and the browser actually tearing down the page,
    // is precisely what let the passive listener's reload() race back in.
    logoutInProgressRef.current = true;
    try {
      // GB #1435 (D2): mark this null transition as an EXPLICIT logout so the
      // onIdTokenChanged(null) listener above is allowed to destroy the auth
      // cookie. Set immediately before signOut() fires that transition.
      isExplicitLogoutRef.current = true;
      await signOut(AUTH);
      setUser(null);
      setUserDoc(null);
      Cookie.remove('auth');
      clearSessionId();
      // Codex P1 fix (GB #1373): detach the user-doc listener here too, not
      // just via the onIdTokenChanged listener above. signOut() resolving
      // doesn't guarantee that listener's user===null branch has already run
      // (same race the existing duplication in this function already accounts
      // for) — calling an already-detached listener's unsubscribe again is a
      // documented no-op in Firestore, so this is safe either order.
      userDocUnsubscribeRef.current?.();
      userDocUnsubscribeRef.current = null;
      // Cleared here (not just via the onIdTokenChanged listener above) so
      // there's no window where a post-logout click could still carry the
      // previous user's Advanced Matching identity. Matches the existing
      // Cookie.remove('auth') duplication pattern in this function.
      //
      // Codex P2 fix: isolated in its own try/catch. A broken/hostile pixel
      // (fbq exists but throws) must never reject this function's promise —
      // logout() has already called signOut(AUTH) by this point, so a thrown,
      // uncaught error here would surface as a "failed" logout to the caller
      // even though auth sign-out already succeeded.
      try {
        clearMetaAdvancedMatching();
      } catch (clearError) {
        logError(clearError);
      }
      // Drop any in-flight checkout PI so the next user (guest or different
      // account) doesn't inherit it. The /checkout page also validates uid
      // on read, but clearing here keeps sessionStorage tidy.
      try { sessionStorage.removeItem('gf_checkout_pi'); } catch {}
      // TASK-1373 / GB #1206: same rationale as the PI clear above — a
      // guest-captured checkout email must not survive a logout on this
      // browser into the next shopper's session.
      clearGuestIdentity();

      // Phase A-E wire evidence (2026-07-15, live on gearfocus.com — see
      // clearMetaAdvancedMatching's doc comment in analytics.service.ts):
      // Meta's browser pixel is FIRST-WRITE-WINS. There is no in-page way to
      // clear Advanced Matching identity once it's attached to the pixel — the
      // clearMetaAdvancedMatching() call above only cancels a pending retry,
      // it cannot un-attach an identity that already landed. A hard navigation
      // is the only real clear. Logout is exactly the moment a shared/kiosk
      // browser must not keep carrying the outgoing user's identity into the
      // next (anonymous or different-account) session, so this ALWAYS
      // hard-navigates home — unconditional, not gated on
      // isPixelIdentityAttached(), because logout is a rare, deliberate action
      // and "always" is simpler and safer than a conditional that could be
      // wrong. UX tradeoff, stated plainly: every logout click is now a full
      // page load, not a client-side transition. All state cleanup above
      // (signOut, listener detach, cookie/session/guest-identity clears) has
      // already completed synchronously by this point, so nothing is lost by
      // leaving immediately. Verified safe against every logout call site —
      // DashboardSideMenu, UserMenu, and MenuDrawer all call this fire-and-
      // forget with no code after that depends on staying on the current page;
      // two of the three already render as a Link/Button with href="/",
      // meaning "navigate home on logout" was already the intended UX, just
      // previously via Next.js client-side routing instead of a hard nav.
      //
      // Codex P1 fix (final, GB #1373): this function only covers the SAME
      // tab that clicked logout. A cross-tab logout (this tab's signOut()
      // pushes user===null through Firebase's shared auth state into every
      // OTHER open tab too) is handled separately in the onIdTokenChanged
      // listener above — gated on isPixelIdentityAttached() there, AND on
      // logoutInProgressRef being false, since that listener also fires in
      // THIS same tab as a direct result of the signOut() call above (see
      // logoutInProgressRef's doc comment) — without that second gate, its
      // reload() could race this assign('/') call.
      //
      // No reset of logoutInProgressRef after this line — see the comment
      // at the top of this function. The flag intentionally stays true for
      // the remainder of this tab's life on the success path.
      window.location.assign('/');
    } catch (error) {
      // Codex P2 correction (final round, GB #1373): logout() genuinely
      // failed (most likely signOut() itself, the first line in the try
      // block above — every other throwable call in this function already
      // has its own inner try/catch) — this tab is NOT navigating away, so
      // leaving the flag stuck true would permanently disable the cross-tab
      // reload protection in this tab for the rest of the session. Reset it
      // here, where it's actually safe to (the tab lives on), then
      // re-throw so the caller still observes the failure.
      logoutInProgressRef.current = false;
      // GB #1435 (D2): signOut() failed, so no null transition will fire to
      // consume this flag — reset it here or a later transient null would
      // wrongly destroy a still-valid cookie.
      isExplicitLogoutRef.current = false;
      throw error;
    }
  };

  const sendPasswordResetEmail = async (email: string) => {
    const actionCodeSettings: ActionCodeSettings = {
      url: `${getHostUrl()}/reset-password`,
    };
    await _sendPasswordResetEmail(AUTH, email, actionCodeSettings);
  };

  const resetPassword = async (oobCode: string, password: string) => {
    await confirmPasswordReset(AUTH, oobCode, password);
  };

  const updateUserPassword = async (
    oldPassword: string,
    newPassword: string
  ) => {
    if (AUTH.currentUser?.email && isAccountEmailPassword()) {
      const credential = EmailAuthProvider.credential(
        AUTH.currentUser.email,
        oldPassword
      );
      try {
        await reauthenticateWithCredential(AUTH.currentUser, credential);
        await updatePassword(AUTH.currentUser, newPassword);
      } catch (err) {
        throw new Error('Incorrect password');
      }
    }
  };

  const isAccountEmailPassword = () => {
    if (AUTH.currentUser) {
      return AUTH.currentUser.providerData.some(
        (p) => p.providerId === 'password'
      );
    }
    return false;
  };

  const fetchProviderForEmail = async (email: string) => {
    try {
      const methods = await fetchSignInMethodsForEmail(AUTH, email);
      if (methods?.length) {
        const method =
          methods[0] === 'password'
            ? 'your email and password'
            : methods[0].charAt(0).toUpperCase() +
              methods[0].substring(1).replace('.com', '');
        return method;
      }
    } catch (_e) {
      // invalid email
    }
    return null;
  };

  const updateUserEmail = (email: string) => {
    if (AUTH.currentUser) {
      updateEmail(AUTH.currentUser, email);
    }
  };

  // GB #1471: forces a server read of the user doc instead of waiting on the
  // onIdTokenChanged effect's onSnapshot listener, which is the ONLY thing
  // that otherwise updates userDoc. Cloud Function writes (like
  // setVacationMode) have no client-side latency compensation, so if the
  // push stream stalls the UI keeps showing the pre-write state until a
  // hard refresh — unlike direct client Firestore writes, which the
  // listener echoes locally right away and which therefore do NOT need
  // this. Callers that need the UI to agree with a CF write should await
  // this before showing success. Scope note: this updates userDoc ONLY —
  // it deliberately skips the listener's side effects (Sentry.setUser,
  // omnisend/meta identify, syncEmailVerified, presence), so it is not a
  // substitute for the listener on identity-shaped fields. Never throws,
  // and resolves within ~4s even if Firestore's retry window is longer —
  // the caller gets `false` on timeout while the read continues in the
  // background and still applies (uid-guarded) if it eventually lands.
  const refreshUserDoc = useCallback(async (): Promise<boolean> => {
    if (!user?.uid) return false;
    const applyServerRead = async (): Promise<boolean> => {
      try {
        const doc = await fetchUserDocumentFromServer(user.uid);
        // The auth user can change while the read is in flight (cross-tab
        // login, impersonation start/end, token expiry). Same hazard the
        // uid-mismatch guard in the onIdTokenChanged effect exists for:
        // without this check, a slow read for the OLD uid would clobber the
        // NEW user's doc — and their listener won't re-fire to correct it
        // until their doc next changes.
        if (AUTH.currentUser?.uid !== user.uid) return false;
        if (!doc) {
          // Server-confirmed absence (or a transient miss) — leave the
          // existing userDoc state alone rather than clearing it. The
          // onSnapshot listener's server-confirmed-absence branch (see
          // listenToUserDocument) is the source of truth for genuine ghost
          // accounts; this path must not race it.
          return false;
        }
        // GB #1534 Phase 2 (qa-review F3): this is a third delivery path
        // alongside the listener's onDoc and the retry wrapper's own
        // clearing — the userDocError doc comment promises it clears "on
        // the next delivery through a fresh subscription, or on logout,"
        // but a server-confirmed recovery via this function is also a
        // genuine delivery and must clear it the same way.
        userDocRef.current = doc;
        setUserDoc(doc);
        setUserDocError(false);
        return true;
      } catch (err) {
        console.warn(
          '[auth-lifecycle] refresh-user-doc-failed',
          JSON.stringify({ uid: user.uid })
        );
        Sentry.addBreadcrumb({
          category: 'auth-lifecycle',
          message: 'refresh-user-doc-failed',
          level: 'warning',
          data: { uid: user.uid },
        });
        return false;
      }
    };
    // On a flaky connection getDocFromServer can spend many seconds in
    // Firestore's retry window before rejecting. The write the caller is
    // confirming already succeeded, so don't hold their UI hostage: give
    // the read ~4s to land, then release the caller with `false` and let
    // the read finish (and apply) in the background.
    return Promise.race([
      applyServerRead(),
      new Promise<boolean>((resolve) =>
        setTimeout(() => resolve(false), 4000)
      ),
    ]);
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        userDoc,
        isLoadingUserDoc,
        userDocError,
        isLoggingIn,
        logout,
        loginEmailPassword,
        loginSocial,
        signInWithToken,
        sendPasswordlessLink,
        signInPasswordlessWithLink,
        createAccountEmailPassword,
        sendEmailVerification,
        sendPasswordResetEmail,
        resetPassword,
        updateUserPassword,
        isAccountEmailPassword,
        verifyEmail,
        fetchProviderForEmail,
        updateUserEmail,
        refreshUserDoc,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthProvider;
